import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { toEngineSettings } from '../domain/model';
import { weekReview } from '../domain/review';

/** Revisión semanal: cifras y reflexiones elegidas, sin valoraciones. */
export function ReviewScreen() {
  const { t, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const r = useMemo(
    () =>
      weekReview({
        habits: data.habits,
        idx,
        vacations: data.vacations,
        focus: data.focus,
        journal: data.journal,
        settings,
        tz: ctx.tz,
        dayStartMinutes: ctx.settings.dayStartMinutes,
        today: ctx.today,
        weekOffset: offset,
      }),
    [data.habits, idx, data.vacations, data.focus, data.journal, settings, ctx.tz, ctx.settings.dayStartMinutes, ctx.today, offset],
  );
  const pct = (v: number | null) => (v === null ? t('common.dash') : `${Math.round(v * 100)} %`);
  const range = `${fmtDate(r.start, { day: 'numeric', month: 'short' })} – ${fmtDate(r.end, { day: 'numeric', month: 'short' })}`;

  return (
    <div className="stack-l">
      <header className="page-header" style={{ marginBottom: 0 }}>
        <button type="button" className="icon-btn" aria-label={t('common.back')} onClick={() => navigate(-1)}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1 style={{ flex: 1 }}>{t('review.title')}</h1>
      </header>

      <nav className="row" aria-label={t('review.weekNav')} style={{ justifyContent: 'space-between' }}>
        <button type="button" className="icon-btn" aria-label={t('review.prevWeek')} onClick={() => setOffset(offset - 1)}>
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <div style={{ textAlign: 'center' }}>
          <strong aria-live="polite">{range}</strong>
          {r.inProgress && <div className="muted xsmall">{t('review.inProgress')}</div>}
        </div>
        <button type="button" className="icon-btn" aria-label={t('review.nextWeek')} disabled={offset >= 0} onClick={() => setOffset(offset + 1)}>
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </nav>

      <section className="card stack" aria-labelledby="review-habits">
        <h2 id="review-habits">{t('review.habits')}</h2>
        <p className="num" style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, margin: 0 }}>
          {pct(r.consistency)}
        </p>
        <p className="small" style={{ margin: 0 }}>
          {r.eligible > 0 ? t('review.consistency', { done: r.completed, total: r.eligible }) : t('review.noPeriods')}
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          {t('review.previous', { value: pct(r.previousConsistency) })}
        </p>
        {r.habits.length > 0 && (
          <ul className="small" style={{ margin: 0, paddingLeft: '1.2em' }}>
            {r.habits.map((h) => (
              <li key={h.habitId}>
                {h.name}: <span className="num">{t('review.habitLine', { done: h.completed, total: h.eligible })}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="muted xsmall" style={{ margin: 0 }}>
          {t('review.howCounted')}
        </p>
      </section>

      <section className="card stack" aria-labelledby="review-other">
        <h2 id="review-other">{t('review.other')}</h2>
        <p className="num" style={{ margin: 0 }}>
          {t('review.tasks', { count: r.tasksCompleted })}
        </p>
        <p className="num" style={{ margin: 0 }}>
          {t('review.focus', { minutes: r.focusMinutes, count: r.focusSessions })}
        </p>
      </section>

      <section className="card stack" aria-labelledby="review-reflections">
        <h2 id="review-reflections">{t('review.reflections')}</h2>
        {r.reflections.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            {t('review.noReflections')}
          </p>
        ) : (
          r.reflections.map((j) => (
            <article key={j.id} className="stack" aria-label={fmtDate(j.date, { weekday: 'long', day: 'numeric', month: 'long' })}>
              <strong className="small">{fmtDate(j.date, { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
              {j.text && <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 }}>{j.text}</p>}
              {j.prompts.map((p) => (
                <div key={p.key} className="small">
                  <em>{t(`journal.prompts.${p.key}` as 'journal.prompts.went_well')}</em> {p.answer}
                </div>
              ))}
            </article>
          ))
        )}
      </section>
    </div>
  );
}
